import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";

// DPDP Act 2023: notice + consent BEFORE any analytics/tracking runs.
export const ConsentBanner = ({ onDecision }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("hs_privacy_consent");
    if (!stored) setVisible(true);
  }, []);

  const decide = (choice) => {
    localStorage.setItem("hs_privacy_consent", choice);
    localStorage.setItem("hs_privacy_consent_at", new Date().toISOString());
    setVisible(false);
    onDecision(choice);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[65] p-3 sm:p-5"
          data-testid="consent-banner"
        >
          <div className="max-w-3xl mx-auto rounded-2xl border border-white/15 bg-[rgba(13,14,17,0.97)] backdrop-blur-md shadow-[var(--shadow-elev-2)] p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-9 w-9 shrink-0 rounded-xl bg-[rgba(202,255,26,0.12)] border border-[rgba(202,255,26,0.3)] flex items-center justify-center">
                <ShieldCheck className="h-4.5 w-4.5 text-[color:var(--hs-lime)]" size={18} />
              </span>
              <div className="min-w-0">
                <p className="text-sm text-white/90 font-medium">Your data, your call.</p>
                <p className="mt-1 text-xs text-white/55 leading-relaxed">
                  We'd like to use minimal, pseudonymous analytics (page visits, campaign source) to improve this site,
                  as per the Digital Personal Data Protection Act, 2023. No tracking runs until you agree.{" "}
                  <Link to="/privacy" className="text-[color:var(--hs-lime)] underline underline-offset-2" data-testid="consent-privacy-link">
                    Privacy Policy
                  </Link>
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={() => decide("accepted")}
                    data-testid="consent-accept-button"
                    className="h-9 px-5 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl text-sm btn-lift"
                  >
                    Accept
                  </Button>
                  <Button
                    onClick={() => decide("declined")}
                    variant="outline"
                    data-testid="consent-decline-button"
                    className="h-9 px-5 bg-transparent border-white/20 text-white/80 hover:bg-white/5 hover:text-white rounded-xl text-sm"
                  >
                    Decline
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
